import {
  DiagramBounds,
  computeDiagramContentBounds,
  computeLifelineBottom,
} from './contentBounds';
import {
  estimateSequenceMessageFootprint,
} from './sequenceMessageLayout';
import {
  DiagramPage,
  DiagramType,
  Edge,
  Layer,
  Node,
  NodeGroup,
  PageSettings,
  SequenceFragment,
  SequenceMessage,
  SequenceParticipant,
  SequenceParticipantGroup,
} from '../types';

const ROOT_GRAPH_ID = 'root';
const PARTICIPANT_HEADER_WIDTH = 140;
const GROUP_COLLAPSED_WIDTH = 220;
const GROUP_HEADER_HEIGHT = 40;
const GROUP_SOCKET_ROW_HEIGHT = 22;
const GROUP_BOTTOM_PADDING = 12;
const SELF_MESSAGE_WIDTH = 48;
const SELF_MESSAGE_HEIGHT = 36;
const SEQUENCE_MIN_LABEL_WIDTH = 96;
const MIN_EXPORT_PADDING = 96;
const EXPORT_PADDING_RATIO = 0.08;
const MIN_EXPORT_LONGEST_PX = 2200;
const TARGET_EXPORT_LONGEST_PX = 3600;
const MAX_EXPORT_DIMENSION_PX = 6400;
const MAX_EXPORT_PIXEL_COUNT = 28_000_000;
const MAX_EXPORT_SCALE = 3;

export interface ExportDiagramSnapshot {
  id?: string;
  name: string;
  diagramType: DiagramType;
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
  /** The page's own overrides, so an export looks like the page it came from. */
  settings?: PageSettings;
}

export interface ExportStageLayout {
  bounds: DiagramBounds;
  width: number;
  height: number;
  scale: number;
  padding: number;
  viewport: {
    x: number;
    y: number;
    scale: number;
  };
}

function cloneNode(node: Node): Node {
  return {
    ...node,
    position: { ...node.position },
    size: { ...node.size },
    sockets: node.sockets.map((socket) => ({
      ...socket,
      position: { ...socket.position },
    })),
    meta: node.meta
      ? {
          ...node.meta,
          links: node.meta.links?.map((link) => ({ ...link })),
        }
      : node.meta,
    data: node.data ? JSON.parse(JSON.stringify(node.data)) : node.data,
  };
}

function cloneEdge(edge: Edge): Edge {
  return {
    ...edge,
    waypoints: edge.waypoints?.map((waypoint) => ({ ...waypoint })),
  };
}

function cloneGroup(group: NodeGroup): NodeGroup {
  return {
    ...group,
    position: { ...group.position },
    size: { ...group.size },
    childNodeIds: [...group.childNodeIds],
    childGroupIds: [...(group.childGroupIds ?? [])],
  };
}

function cloneLayer(layer: Layer): Layer {
  return { ...layer };
}

function cloneParticipant(participant: SequenceParticipant): SequenceParticipant {
  return {
    ...participant,
    links: participant.links?.map((link) => ({ ...link })),
  };
}

function cloneMessage(message: SequenceMessage): SequenceMessage {
  return {
    ...message,
    links: message.links?.map((link) => ({ ...link })),
  };
}

function cloneFragment(fragment: SequenceFragment): SequenceFragment {
  return { ...fragment };
}

function cloneParticipantGroup(group: SequenceParticipantGroup): SequenceParticipantGroup {
  return {
    ...group,
    childParticipantIds: [...group.childParticipantIds],
  };
}

function defaultBounds(): DiagramBounds {
  return { minX: 0, maxX: 800, minY: 0, maxY: 600 };
}

function includePoint(bounds: DiagramBounds, x: number, y: number): void {
  bounds.minX = Math.min(bounds.minX, x);
  bounds.maxX = Math.max(bounds.maxX, x);
  bounds.minY = Math.min(bounds.minY, y);
  bounds.maxY = Math.max(bounds.maxY, y);
}

function includeRect(bounds: DiagramBounds, x: number, y: number, width: number, height: number): void {
  includePoint(bounds, x, y);
  includePoint(bounds, x + width, y + height);
}

function cloneBounds(bounds: DiagramBounds | null): DiagramBounds {
  if (!bounds) return defaultBounds();
  return { ...bounds };
}

function getVisibleLayer(layers: Layer[], layerId?: string): Layer | undefined {
  return layers.find((layer) => layer.id === (layerId ?? 'default'))
    ?? layers.find((layer) => layer.id === 'default');
}

function getCollapsedGroupSocketRows(group: NodeGroup, nodes: Node[]): number {
  const inputNode = group.groupInputNodeId
    ? nodes.find((node) => node.id === group.groupInputNodeId)
    : undefined;
  const outputNode = group.groupOutputNodeId
    ? nodes.find((node) => node.id === group.groupOutputNodeId)
    : undefined;
  const inputCount = inputNode?.sockets.filter((socket) => socket.type === 'input').length ?? 0;
  const outputCount = outputNode?.sockets.filter((socket) => socket.type === 'output').length ?? 0;
  return Math.max(1, inputCount, outputCount);
}

function expandFlowBounds(bounds: DiagramBounds, snapshot: ExportDiagramSnapshot): void {
  snapshot.groups.forEach((group) => {
    const treatAsCollapsed = group.isFrame ? group.collapsed : true;
    if (!treatAsCollapsed) return;

    const rows = getCollapsedGroupSocketRows(group, snapshot.nodes);
    const collapsedHeight = GROUP_HEADER_HEIGHT + rows * GROUP_SOCKET_ROW_HEIGHT + GROUP_BOTTOM_PADDING;
    includeRect(bounds, group.position.x, group.position.y, GROUP_COLLAPSED_WIDTH, collapsedHeight);
  });
}

function expandSequenceBounds(bounds: DiagramBounds, snapshot: ExportDiagramSnapshot): void {
  const isParticipantVisible = (participant: { layerId?: string }) => {
    const layer = getVisibleLayer(snapshot.layers, participant.layerId);
    return layer?.visible ?? true;
  };

  const collapsedParticipantIds = new Set(
    snapshot.participantGroups
      .filter((group) => group.collapsed)
      .flatMap((group) => group.childParticipantIds),
  );

  const visibleParticipants = snapshot.participants.filter((participant) =>
    isParticipantVisible(participant) && !collapsedParticipantIds.has(participant.id),
  );

  const proxyParticipants = snapshot.participantGroups
    .filter((group) => group.collapsed)
    .map((group) => {
      const children = snapshot.participants.filter((participant) => group.childParticipantIds.includes(participant.id));
      if (children.length === 0) return null;
      const avgX = children.reduce((sum, participant) => sum + participant.x, 0) / children.length;
      return {
        id: group.id,
        type: children[0].type,
        label: group.label,
        color: group.color,
        order: Math.min(...children.map((participant) => participant.order)),
        x: avgX,
      } as SequenceParticipant;
    })
    .filter((participant): participant is SequenceParticipant => participant !== null);

  const allVisibleParticipants = [...visibleParticipants, ...proxyParticipants];
  const resolveParticipant = (participantId: string) => {
    if (collapsedParticipantIds.has(participantId)) {
      const group = snapshot.participantGroups.find((entry) =>
        entry.collapsed && entry.childParticipantIds.includes(participantId),
      );
      if (group) {
        return allVisibleParticipants.find((participant) => participant.id === group.id) ?? null;
      }
    }
    return allVisibleParticipants.find((participant) => participant.id === participantId) ?? null;
  };

  const lifelineBottom = computeLifelineBottom(
    snapshot.messages,
    snapshot.fragments,
    snapshot.seqLifelineLength,
  );

  snapshot.participantGroups
    .filter((group) => !group.collapsed)
    .forEach((group) => {
      const children = snapshot.participants.filter((participant) =>
        group.childParticipantIds.includes(participant.id) && isParticipantVisible(participant),
      );
      if (children.length === 0) return;
      const minX = Math.min(...children.map((participant) => participant.x)) - 20;
      const maxX = Math.max(...children.map((participant) => participant.x + PARTICIPANT_HEADER_WIDTH)) + 20;
      includeRect(bounds, minX, 20, maxX - minX, lifelineBottom + 60);
    });

  const getNestLevel = (fragmentId: string, depth = 0): number => {
    const fragment = snapshot.fragments.find((entry) => entry.id === fragmentId);
    if (!fragment?.parentFragmentId) return depth;
    return getNestLevel(fragment.parentFragmentId, depth + 1);
  };

  snapshot.fragments.forEach((fragment) => {
    const fragmentParticipants = snapshot.participants.filter((participant) =>
      fragment.participantIds.includes(participant.id),
    );
    if (fragmentParticipants.length === 0) return;
    const nestLevel = getNestLevel(fragment.id);
    const inset = nestLevel * 12;
    const minX = Math.min(...fragmentParticipants.map((participant) => participant.x));
    const maxX = Math.max(...fragmentParticipants.map((participant) => participant.x + PARTICIPANT_HEADER_WIDTH));
    includeRect(
      bounds,
      minX - 20 + inset,
      fragment.startY,
      maxX - minX + 40 - inset * 2,
      fragment.endY - fragment.startY,
    );
  });

  snapshot.messages.forEach((message) => {
    const source = resolveParticipant(message.sourceId);
    const target = resolveParticipant(message.targetId);
    if (!source || !target) return;

    const footprint = estimateSequenceMessageFootprint(
      allVisibleParticipants,
      message.label,
      source.id,
      target.id,
      { minLabelWidth: SEQUENCE_MIN_LABEL_WIDTH },
    );
    const labelTopY = message.y - footprint.above;
    const labelHeight = footprint.above + footprint.below;
    const sourceCenterX = source.x + PARTICIPANT_HEADER_WIDTH / 2;
    const targetCenterX = target.x + PARTICIPANT_HEADER_WIDTH / 2;

    if (source.id === target.id) {
      includeRect(bounds, sourceCenterX, message.y, SELF_MESSAGE_WIDTH, SELF_MESSAGE_HEIGHT);
      includeRect(bounds, sourceCenterX + 4, labelTopY, footprint.maxLabelWidth, labelHeight);
      return;
    }

    const actualSourceX = targetCenterX > sourceCenterX ? sourceCenterX + 4 : sourceCenterX - 4;
    const arrowTipX = targetCenterX;
    const labelRangeMinX = Math.min(actualSourceX, arrowTipX);
    const lineSpan = Math.max(40, Math.abs(arrowTipX - actualSourceX));
    const labelX = labelRangeMinX + (lineSpan - footprint.maxLabelWidth) / 2;
    includeRect(bounds, labelX, labelTopY, footprint.maxLabelWidth, labelHeight);
  });
}

export function buildExportSnapshotFromPage(page: DiagramPage): ExportDiagramSnapshot {
  const rootGraph = page.graphs?.[ROOT_GRAPH_ID];
  return {
    id: page.id,
    name: page.name,
    diagramType: page.diagramType ?? 'flow',
    nodes: (rootGraph?.nodes ?? page.nodes).map(cloneNode),
    edges: (rootGraph?.edges ?? page.edges).map(cloneEdge),
    groups: page.groups.map(cloneGroup),
    layers: page.layers.map(cloneLayer),
    participants: (page.participants ?? []).map(cloneParticipant),
    messages: (page.messages ?? []).map(cloneMessage),
    fragments: (page.fragments ?? []).map(cloneFragment),
    participantGroups: (page.participantGroups ?? []).map(cloneParticipantGroup),
    seqLifelineLength: page.seqLifelineLength ?? 500,
    settings: page.settings ? { ...page.settings } : undefined,
  };
}

export function computeExportBounds(snapshot: ExportDiagramSnapshot): DiagramBounds {
  const bounds = cloneBounds(
    computeDiagramContentBounds({
      nodes: snapshot.nodes,
      edges: snapshot.edges,
      groups: snapshot.groups,
      participants: snapshot.participants,
      messages: snapshot.messages,
      fragments: snapshot.fragments,
      seqLifelineLength: snapshot.seqLifelineLength,
    }),
  );

  if (snapshot.diagramType === 'sequence') {
    expandSequenceBounds(bounds, snapshot);
  } else {
    expandFlowBounds(bounds, snapshot);
  }

  return bounds;
}

export function computeExportStageLayout(snapshot: ExportDiagramSnapshot): ExportStageLayout {
  const bounds = computeExportBounds(snapshot);
  const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(1, bounds.maxY - bounds.minY);
  const padding = Math.max(
    MIN_EXPORT_PADDING,
    Math.round(Math.max(contentWidth, contentHeight) * EXPORT_PADDING_RATIO),
  );

  const paddedWidth = contentWidth + padding * 2;
  const paddedHeight = contentHeight + padding * 2;
  const longestSide = Math.max(paddedWidth, paddedHeight);
  let scale = Math.min(
    MAX_EXPORT_SCALE,
    Math.max(1, TARGET_EXPORT_LONGEST_PX / Math.max(1, longestSide)),
  );

  if (longestSide * scale < MIN_EXPORT_LONGEST_PX) {
    scale = Math.min(MAX_EXPORT_SCALE, MIN_EXPORT_LONGEST_PX / Math.max(1, longestSide));
  }

  if (longestSide * scale > MAX_EXPORT_DIMENSION_PX) {
    scale = Math.min(scale, MAX_EXPORT_DIMENSION_PX / Math.max(1, longestSide));
  }

  const scaledPixelCount = paddedWidth * paddedHeight * scale * scale;
  if (scaledPixelCount > MAX_EXPORT_PIXEL_COUNT) {
    scale = Math.min(scale, Math.sqrt(MAX_EXPORT_PIXEL_COUNT / Math.max(1, paddedWidth * paddedHeight)));
  }

  if (!Number.isFinite(scale) || scale <= 0) {
    scale = 1;
  }

  return {
    bounds,
    width: Math.max(1, Math.ceil(paddedWidth * scale)),
    height: Math.max(1, Math.ceil(paddedHeight * scale)),
    scale,
    padding,
    viewport: {
      x: (padding - bounds.minX) * scale,
      y: (padding - bounds.minY) * scale,
      scale,
    },
  };
}
